import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useGetMyAccess, rbacQueryKeys } from '@hooks/rbac/use-roles';
import { MODULES, moduleForPath } from '@constants/modules';
import type { EffectiveAccess, MenuAccessLevel } from '@shared-types/rbac';

interface PermissionsContextType {
  access: EffectiveAccess | undefined;
  permissions: string[];
  /** Deep links of the sidebar entries this user's role grants. */
  menuLinks: string[];
  isLoading: boolean;
  /**
   * True when the user has no custom role and the backend fell back to their
   * legacy role, or when the lookup failed. Callers must fail open in this
   * state so a rollout never removes access somebody already had.
   */
  isFallback: boolean;
  hasPermission: (permissionCode: string) => boolean;
  hasAnyPermission: (permissionCodes: string[]) => boolean;
  canAccessMenu: (link: string) => boolean;
  /**
   * The level this user holds for a module, by company-feature code
   * ("kepabeanan") or by any path inside it ("/doc-pabean/draft"). Undefined
   * when the module is not gated for this user at all.
   */
  accessLevel: (moduleOrPath: string) => MenuAccessLevel | undefined;
  /**
   * Whether write actions on a module should be offered. Mirrors the API's
   * RequireMenuAccess exactly — same fail-open states, same read/full split —
   * so a button this enables is a call the server will answer.
   */
  canWrite: (moduleOrPath: string) => boolean;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

/**
 * `pabean:*` grants every action in the module, and the legacy full-access
 * codes grant everything — mirrors `constants.Satisfies` on the backend so the
 * UI never shows a button the API would reject (or hides one it would allow).
 */
const LEGACY_SUPER_PERMISSIONS = ['ALL', 'ALL_MST'];

/** The Custom Roles screen — always reachable with `rbac:manage`, see below. */
const RBAC_ADMIN_LINK = '/customer-approval/roles';

const MODULE_CODE_SET = new Set<string>(MODULES.map((module) => module.code));

/** Accepts a feature code directly, or resolves a route path to one. */
function resolveModuleCode(moduleOrPath: string): string | undefined {
  if (!moduleOrPath) return undefined;
  if (MODULE_CODE_SET.has(moduleOrPath)) return moduleOrPath;
  return moduleForPath(moduleOrPath)?.code;
}

function satisfies(held: string, required: string): boolean {
  if (held === required) return true;
  if (LEGACY_SUPER_PERMISSIONS.includes(held)) return true;
  const separator = required.indexOf(':');
  if (separator > 0 && held === `${required.slice(0, separator)}:*`) return true;
  return false;
}

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data: access, isLoading, isError } = useGetMyAccess();

  // Impersonation swaps the effective user without a reload, so the resolved
  // permissions have to follow.
  useEffect(() => {
    const onImpersonationChange = () => {
      queryClient.invalidateQueries({ queryKey: rbacQueryKeys.me });
    };
    window.addEventListener('impersonationChanged', onImpersonationChange);
    return () => window.removeEventListener('impersonationChanged', onImpersonationChange);
  }, [queryClient]);

  const permissions = useMemo(() => access?.permissions ?? [], [access]);
  const menuLinks = useMemo(() => access?.menuLinks ?? [], [access]);

  // Fail open while loading, on error, and for legacy users: the pre-RBAC
  // behaviour is "everything the app rendered before", and that must not
  // change until an admin deliberately assigns a custom role.
  const isFallback = isLoading || isError || !access || access.isFallback || access.source === 'legacy';

  const hasPermission = useCallback(
    (permissionCode: string) => {
      if (isFallback) return true;
      return permissions.some((held) => satisfies(held, permissionCode));
    },
    [isFallback, permissions]
  );

  const hasAnyPermission = useCallback(
    (permissionCodes: string[]) => {
      if (isFallback) return true;
      if (permissionCodes.length === 0) return true;
      return permissionCodes.some((code) => permissions.some((held) => satisfies(held, code)));
    },
    [isFallback, permissions]
  );

  // Legacy full access. The backend's `Satisfies` lets ALL/ALL_MST clear any
  // permission check, and the sidebar has to agree: without this an admin
  // holding ALL is still menu-gated, and the first role that trims their menus
  // takes away the Custom Roles screen they would fix it from.
  const hasSuperPermission = useMemo(
    () => permissions.some((held) => LEGACY_SUPER_PERMISSIONS.includes(held)),
    [permissions]
  );

  // An empty menu list means "no menus" only when the role actually chose its
  // menus — `restrictMenus`. The rollout migration mirrors every legacy role
  // into a custom role and assigns all existing users to it, copying menus
  // from the pre-RBAC `role_menus` table; that table is empty on installs that
  // never used the legacy menu screens, so those mirrors carry no menu links
  // at all. Enforcing them would lock out every backfilled user rather than
  // just the role an admin deliberately left empty.
  const canAccessMenu = useCallback(
    (link: string) => {
      if (isFallback || !access?.restrictMenus || hasSuperPermission) return true;
      if (!link || link === '#') return true;
      const normalized = link.split('?')[0];
      // Never gate away the screen that grants menu access, or a role that
      // locks its own author out is unrecoverable without a DB edit.
      if (normalized === RBAC_ADMIN_LINK && permissions.some((held) => satisfies(held, 'rbac:manage'))) return true;
      return menuLinks.some((granted) => granted === link || granted.split('?')[0] === normalized);
    },
    [isFallback, access?.restrictMenus, hasSuperPermission, menuLinks, permissions]
  );

  const modules = useMemo(() => access?.modules ?? {}, [access]);

  const accessLevel = useCallback(
    (moduleOrPath: string): MenuAccessLevel | undefined => {
      const code = resolveModuleCode(moduleOrPath);
      if (!code) return undefined;
      // Same escapes as canAccessMenu: a user the gate lets through
      // unconditionally has full access, not an undefined level.
      if (isFallback || !access?.restrictMenus || hasSuperPermission) return 'full';
      return modules[code];
    },
    [access?.restrictMenus, hasSuperPermission, isFallback, modules]
  );

  const canWrite = useCallback(
    (moduleOrPath: string) => {
      const level = accessLevel(moduleOrPath);
      // A module with no level is one this build does not gate — offering its
      // actions keeps the UI honest about what the API will accept.
      return level !== 'read';
    },
    [accessLevel]
  );

  const value = useMemo<PermissionsContextType>(
    () => ({
      access,
      permissions,
      menuLinks,
      isLoading,
      isFallback,
      hasPermission,
      hasAnyPermission,
      canAccessMenu,
      accessLevel,
      canWrite,
    }),
    [
      access,
      permissions,
      menuLinks,
      isLoading,
      isFallback,
      hasPermission,
      hasAnyPermission,
      canAccessMenu,
      accessLevel,
      canWrite,
    ]
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

/**
 * Safe outside the provider: returns a permissive stub so a component rendered
 * from a non-dashboard tree (auth pages, the reviewer portal) never crashes.
 */
export function usePermissions(): PermissionsContextType {
  const context = useContext(PermissionsContext);
  if (context) return context;
  return {
    access: undefined,
    permissions: [],
    menuLinks: [],
    isLoading: false,
    isFallback: true,
    hasPermission: () => true,
    hasAnyPermission: () => true,
    canAccessMenu: () => true,
    accessLevel: () => 'full',
    canWrite: () => true,
  };
}

/** `const canCreate = useHasPermission('pabean:create')` */
export function useHasPermission(permissionCode: string): boolean {
  const { hasPermission } = usePermissions();
  return hasPermission(permissionCode);
}

export function useHasAnyPermission(permissionCodes: string[]): boolean {
  const { hasAnyPermission } = usePermissions();
  return hasAnyPermission(permissionCodes);
}

/**
 * `const canEdit = useCanWrite('/doc-pabean')` — false only for a role whose
 * grant of that menu is "Lihat saja".
 */
export function useCanWrite(moduleOrPath: string): boolean {
  const { canWrite } = usePermissions();
  return canWrite(moduleOrPath);
}

export default PermissionsContext;
